import { createTheme } from "@mui/material/styles";

// Color palette
const colors = {
  primary: {
    main: "#6933FF", // Electric Indigo
    50: "#7848FF",
    100: "#875CFF",
    200: "#A585FF",
    300: "#C3ADFF",
    400: "#E1D6FF",
    500: "#F0EBFF",
    600: "#0A0519",
    700: "#150A33",
    800: "#2A1466",
    900: "#3F1F99",
    1000: "#5429CC",
    1100: "#5E2EE5",
    white: "#ffffff",
  },
  secondary: {
    main: "#1A99A3", // Dark Cyan
    50: "#31A3AC",
    100: "#48ADB5",
    200: "#76C2C8",
    300: "#A3D6DA",
    400: "#D1EBED",
    500: "#E8F5F6",
    white: "#ffffff",
  },
  neutral: {
    main: "#171717", // Eerie Black
    50: "#2F2F2F",
    100: "#454545",
    200: "#747474",
    300: "#A2A2A2",
    400: "#D1D1D1",
    500: "#E8E8E8",
    600: "#F7F7F7",
    white: "#ffffff",
  },
  success: {
    main: "#2F9801",
    50: "#59AD34",
    100: "#97CC80",
    200: "#E8F4E3",
    white: "#ffffff",
  },
  alert: {
    main: "#FEB101", // Alert
    50: "#FEC134",
    100: "#FFD880",
    200: "#FFF6E3",
  },
  error: {
    main: "#EE2F2D",
    50: "#F15957",
    100: "#F79796",
    200: "#FDE8E8",
  },
  info: {
    main: "#007AFF", // Information
    50: "#3395FF",
    100: "#80BDFF",
    200: "#E6F2FF",
  },
  grey: {
    50: "#FFFFFF",
    100: "#F7F7F7",
    200: "#E8E8E8",
    300: "#D1D1D1",
    400: "#A2A2A2",
    500: "#747474",
    600: "#454545",
    700: "#2F2F2F",
    800: "#171717",
    900: "#171717",
  },
  text: {
    primary: "#171717",
    secondary: "#454545",
    disabled: "#A2A2A2",
    hint: "#747474",
  },
  background: {
    default: "#F7F7F7",
    paper: "#FFFFFF",
  },
  divider: "#E8E8E8",
};

// Typography
const typography = {
  fontFamily: ["Figtree", "sans-serif"].join(","),
  h1: {
    fontSize: "2.5rem",
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: "-0.00833em",
  },
  h2: {
    fontSize: "2rem",
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: "-0.00833em",
  },
  h3: {
    fontSize: "1.75rem",
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: "0em",
  },
  h4: {
    fontSize: "1.5rem",
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: "0.00735em",
  },
  h5: {
    fontSize: "1.25rem",
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: "0em",
  },
  h6: {
    fontSize: "1.125rem",
    fontWeight: 500,
    lineHeight: 1.2,
    letterSpacing: "0.0075em",
  },
  subtitle1: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: "0.00938em",
  },
  subtitle2: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: "0.00714em",
  },
  body1: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: "0.00938em",
  },
  body2: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: "0.01071em",
  },
  button: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: "0.02857em",
    textTransform: "uppercase",
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: "0.03333em",
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: "0.08333em",
    textTransform: "uppercase",
  },
};

// Spacing system - multiples of 4 from 4 to 40
const spacingValues = {
  xs: 4, // 4px
  sm: 8, // 8px
  md: 12, // 12px
  lg: 16, // 16px
  xl: 20, // 20px
  xxl: 24, // 24px
  xxxl: 28, // 28px
  huge: 32, // 32px
  massive: 36, // 36px
  enormous: 40, // 40px
};

// Base spacing unit for MUI theme (4px)
// Material-UI expects spacing to be a function that takes a multiplier
const spacing = (factor) => factor * 4; // 4px base unit

// Component overrides for MUI v6
const components = {
  MuiCssBaseline: {
    styleOverrides: {
      "*": {
        boxSizing: "border-box",
      },
      html: {
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        height: "100%",
        width: "100%",
      },
      body: {
        height: "100%",
        width: "100%",
      },
      "#root": {
        height: "100%",
        width: "100%",
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.sm,
        textTransform: "none",
        fontWeight: 500,
        padding: `${spacingValues.md}px ${spacingValues.xl}px`,
      },
      contained: {
        boxShadow:
          "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
        "&:hover": {
          boxShadow:
            "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
        },
      },
      outlined: {
        borderWidth: 1,
        "&:hover": {
          borderWidth: 1,
        },
      },
    },
    defaultProps: {
      disableRipple: false,
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: spacingValues.sm,
        },
        marginBottom: `${spacingValues.lg}px`,
      },
    },
    defaultProps: {
      variant: "outlined",
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.md,
        boxShadow:
          "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
        padding: `${spacingValues.xl}px`,
      },
    },
    defaultProps: {
      elevation: 1,
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.sm,
      },
      elevation1: {
        boxShadow:
          "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
      },
      elevation2: {
        boxShadow:
          "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
      },
      elevation3: {
        boxShadow:
          "0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)",
      },
      elevation4: {
        boxShadow:
          "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
      },
      elevation5: {
        boxShadow:
          "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)",
      },
    },
    defaultProps: {
      elevation: 1,
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow:
          "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: "1px solid rgba(0, 0, 0, 0.12)",
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
        padding: `${spacingValues.xl}px`,
      },
      head: {
        fontWeight: 500,
        backgroundColor: colors.grey[50],
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&:hover": {
          backgroundColor: colors.grey[50],
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.huge,
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 42,
        height: 26,
        padding: 0,
        margin: spacingValues.sm,
      },
      switchBase: {
        padding: 1,
        "&.Mui-checked": {
          transform: "translateX(16px)",
          color: "#fff",
          "& + .MuiSwitch-track": {
            backgroundColor: colors.success.main,
            opacity: 1,
            border: "none",
          },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": {
          color: colors.success.main,
          border: "6px solid #fff",
        },
      },
      thumb: {
        width: 24,
        height: 24,
      },
      track: {
        borderRadius: 13,
        border: `1px solid ${colors.grey[400]}`,
        backgroundColor: colors.grey[100],
        opacity: 1,
        boxSizing: "border-box",
        transition:
          "background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: spacingValues.sm,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: `${spacingValues.xxl}px ${spacingValues.xxl}px ${spacingValues.xs}px ${spacingValues.xxl}px`,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: `${spacingValues.lg}px ${spacingValues.xxl}px`,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: `${spacingValues.sm}px ${spacingValues.xxl}px ${spacingValues.xxl}px ${spacingValues.xxl}px`,
      },
    },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: {
        "& .MuiSnackbarContent-root": {
          borderRadius: spacingValues.sm,
        },
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.sm,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.sm,
      },
    },
  },
  MuiCircularProgress: {
    styleOverrides: {
      root: {
        color: colors.primary.main,
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: colors.grey[200],
      },
    },
  },
};

// Create the theme
export const appTheme = createTheme({
  palette: colors,
  typography: typography,
  spacing: spacing,
  components: components,
});

// Export spacing values for use in components
export { spacingValues };

export default appTheme;
